/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { first, includes, split, startsWith, toInteger } from 'es-toolkit/compat';

export type AppEnvironment = 'staging' | 'demo' | 'prod';

const propertyWithDefault = (name: string, defaultValue: any) => {
  return (window as any).__TOCK__?.[name] ?? defaultValue;
};

export function isProductionHostname(hostname: string) {
  return hostname === 'dashboard.exploretock.com';
}

export function isLocalHostname(hostname: string) {
  return (
    includes(hostname, 'localhost') ||
    includes(hostname, '127.0.0.1') ||
    startsWith(hostname, '192.168.') ||
    includes(hostname, '10.8.') ||
    includes(hostname, '.local') ||
    (startsWith(hostname, '100.') &&
      toInteger(split(hostname, '.')[1]) >= 64 &&
      toInteger(split(hostname, '.')[1]) <= 127)
  );
}

const hostname = first(split(window.location.hostname, ':')) ?? '';
if (!(window as any).__TOCK__ && !isLocalHostname(hostname)) {
  throw new Error('Cannot load Tock, no settings received');
}

if (!isLocalHostname(hostname) && process.env.APP_ENV !== 'preview') {
  if (
    (includes((window as any).__TOCK__.API_BASE, '-staging') && !includes(hostname, '-staging')) ||
    (includes((window as any).__TOCK__.API_BASE, '-rc') && !includes(hostname, '-rc'))
  ) {
    throw new Error('Cannot load Tock, attempting to load incorrect staging settings');
  }

  if (includes((window as any).__TOCK__.API_BASE, '-demo') && !includes(hostname, '-demo')) {
    throw new Error('Cannot load Tock, attempting to load incorrect demo settings');
  }
}

/**
 * Use the hostname to determine where the app is running
 */
function computeAppEnvironment(): AppEnvironment {
  const { hostname } = window.location;

  if (isLocalHostname(hostname) || hostname.includes('staging') || hostname.includes('-rc')) {
    return 'staging';
  }

  if (hostname.includes('demo')) {
    return 'demo';
  }

  return 'prod';
}

// Common URI constants
const TEST_BASE_URL = 'http://example.com';
// Tock
const LOCAL_API_BASE_URL = `http://${hostname}:6060/api`;
const STAGING_API_BASE_URL = 'https://dashboard-staging.exploretock.com/api';
// JCE/GLOBAL DINING
const LOCAL_BFF_BASE_URL = 'http://localhost:6070';
const STAGING_BFF_BASE_URL = 'https://bff-web.dev.resy.com/';

const isBrowser = BROWSER;
const isCheckin = CHECKIN;

const isDelivery = DELIVERY;

const environment = computeAppEnvironment();

export default {
  // To use your local server, set the system environment variable LOCAL_API_SERVER=true
  API_BASE: propertyWithDefault(
    'API_BASE',
    process.env.JEST_WORKER_ID
      ? TEST_BASE_URL
      : process.env.useLocalServer
        ? LOCAL_API_BASE_URL
        : STAGING_API_BASE_URL
  ),

  APP_ENVIRONMENT: environment,

  BFF_BASE_URL: propertyWithDefault(
    'BFF_BASE_URL',
    process.env.JEST_WORKER_ID
      ? TEST_BASE_URL
      : process.env.useLocalServer
        ? LOCAL_BFF_BASE_URL
        : STAGING_BFF_BASE_URL
  ),

  // The URL base for Braintree transactinons
  BRAINTREE_TRANSACTION_URL_BASE: propertyWithDefault(
    'BRAINTREE_TRANSACTION_URL_BASE',
    'https://sandbox.braintreegateway.com/merchants/36rgt8zsxfj4378f/transactions'
  ),

  BROWSER: isBrowser,

  CHECKIN: isCheckin,

  DELIVERY: isDelivery,

  CONSUMER_APP_URL: propertyWithDefault(
    'CONSUMER_APP_URL',
    process.env.isDevelopment ? `http://${hostname}:7001/api` : 'https://staging.exploretock.com'
  ),

  DASHBOARD_APP_URL: propertyWithDefault(
    'DASHBOARD_APP_URL',
    process.env.isDevelopment
      ? `http://${hostname}:8080`
      : environment === 'prod'
        ? 'https://dashboard.exploretock.com'
        : `https://dashboard-${environment}.exploretock.com`
  ),

  // To enable all features by default, set the system variable ENABLE_FEATURES_BY_DEFAULT=true
  ENABLE_FEATURES_BY_DEFAULT: propertyWithDefault(
    'ENABLE_FEATURES_BY_DEFAULT',
    process.env.enableFeaturesByDefault
  ),

  FULL_STORY_ORG_ID: '2EFSJ',

  GA_PROPERTY_ID: propertyWithDefault('GA_PROPERTY_ID', 'UA-57200812-16'),
  FACEBOOK_PIXEL_ID: propertyWithDefault('FACEBOOK_PIXEL_ID', '1258095530951991'),
  // The Google client API key to use for requests to Google services.
  GOOGLE_API_KEY: propertyWithDefault('GOOGLE_API_KEY', 'AIzaSyCPd-xSabI7QOfKX5NxVVup6bPfcovwFiQ'),

  GOOGLE_MAPS_KEY: propertyWithDefault(
    'GOOGLE_MAPS_KEY',
    'AIzaSyAxNcI-gFR7nGo_MNN5Z644SXJvISYiRDw'
  ),

  IS_DEVELOPMENT: process.env.isDevelopment,

  LAST_TIME_IN_BUSINESS_DAY: '04:00',

  LOG_LEVEL: propertyWithDefault('LOG_LEVEL', 'NETWORKING'),

  PLAID_ENVIRONMENT: propertyWithDefault('PLAID_ENVIRONMENT', 'sandbox'),
  PLAID_PUBLIC_KEY: propertyWithDefault('PLAID_PUBLIC_KEY', '2ef951951a9b169b13869c733a889e'),

  // Rollbar Config info
  ROLLBAR_ACCESS_TOKEN: propertyWithDefault(
    'ROLLBAR_ACCESS_TOKEN',
    '35cc341eb7fd4d84946390431abd5292'
  ),
  ROLLBAR_ENV: propertyWithDefault('ROLLBAR_ENV', 'dev'),

  STRIPE_PUBLIC_KEY: propertyWithDefault(
    'STRIPE_PUBLIC_KEY',
    'pk_test_51Tg5QQK0bopwblSlqf6KOFrjVJNvKDaEYOlDJcHurinYxJEUFUHogEH812ERz6QBXrZNvFQpJk4aQ0dtyAIox5Yq00RhYnuRLx'
  ),
  STRIPE_CONNECT_KEY: propertyWithDefault(
    'STRIPE_CONNECT_KEY',
    'ca_UfQHl3Dx5zCjUaYlMfShisqO3JGD279q'
  ),

  TOCK2US_GA_PROPERTY_ID: propertyWithDefault('GA_PROPERTY_ID', null),

  WSS_BASE: propertyWithDefault(
    'WSS_BASE',
    process.env.useLocalServer
      ? `ws://${hostname}:6061/ws`
      : 'wss://dashboard-staging.exploretock.com/ws'
  ),

  UJET_COMPANY_ID: propertyWithDefault('UJET_COMPANY_ID', '1596232528379853ed51e8c5eeb8144a'),

  UJET_SUBDOMAIN: propertyWithDefault('UJET_SUBDOMAIN', 'tocksb'),

  UJET_LOADER_URL: propertyWithDefault('UJET_LOADER_URL', 'https://websdk.ujetqa.co/v2/loader.js'),
};
